import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from '@if/core/yup'
import { ControlledTextField, ErrorAlert } from '@if/mui'
import { Stack } from '@mui/material'
import { useDeleteInteracWire } from '@src/api/interac-wire-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { InteracWire } from '@src/types/interac'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormDialog from '../Common/Dialog/FormDialog'


interface InteracWireDeleteDialogProps {
  interacWire: InteracWire | null
  onClose: () => void
}

const InteracWireDeleteDialog = ({ interacWire, onClose }: InteracWireDeleteDialogProps) => {
  const { t } = useTranslation()
  const { showSuccess } = useNotification()
  const { mutateAsync: deleteInteracWire, isPending, error, reset: resetMutation } = useDeleteInteracWire()
  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object({
          reason: yup.string().required(),
        }),
      ),
    [],
  )
  const { control, handleSubmit, reset: resetForm } = useForm({ resolver, defaultValues: { reason: '' } })

  const handleOnClose = useCallback(() => {
    if (isPending) return
    resetForm()
    resetMutation()
    onClose()
  }, [isPending, resetForm, resetMutation, onClose])

  const onSubmit = handleSubmit((data) => {
    if (!interacWire || isPending) return
    void deleteInteracWire({
      id: interacWire.id,
      versionTag: interacWire.versionTag,
      reason: data.reason,
    }).then(() => {
      handleOnClose()
      showSuccess()
    })
  })

  return (
    <FormDialog
      open={Boolean(interacWire)}
      onClose={handleOnClose}
      onConfirm={onSubmit}
      title={t('interacWire.deleteTitle')}
      confirmLabel={t('common.delete')}
      confirmColor="error"
      loading={isPending}
    >
      <Stack gap={1}>
        {error && <ErrorAlert error={error} />}
        <ControlledTextField control={control} name="reason" autoFocus fullWidth disabled={isPending} />
      </Stack>
    </FormDialog>
  )
}

export default InteracWireDeleteDialog
